import {
  AgentDetailsLicenceType,
  AgentRebateSettingsMode,
  States,
  type AgentDetails,
  type LicenceDetails,
  type UploadedDocument,
  type Rebate,
} from '@urbanx/agx.agencies.client';
import { createAgxForm, createFormDefinition } from '@urbanx/agx.forms';
import { isValidABN, isValidACN, isValidEmail, toDateOnlyString } from '@urbanx/agx.ui-components2';
import { z } from 'zod';
import { rebatesSchema } from 'components/listing-agreement/lineItemSchemas';
import { rebateFields } from 'components/listing-agreement/rebateFields';
import { DefaultTeamMember } from './DefaultTeamMember';

export const canEditLicenseeDetails = (licenceType: string, states: readonly States[]) =>
  (licenceType === AgentDetailsLicenceType.Class1 && states.includes(States.NSW)) ||
  (licenceType === AgentDetailsLicenceType.RealEstateLicence && states.includes(States.QLD));

export const teamMemberSchema = z
  .object({
    id: z.string(),
    agencyId: z.string(),
    eTag: z.string().optional(),
    firstName: z.string().trim().min(1, 'First name required'),
    lastName: z.string().trim().min(1, 'Last name required'),
    email: z
      .string()
      .refine((value) => isValidEmail(value.trim()), 'Please specify a valid email address'),
    title: z.string(),
    phone: z.string(),
    role: z.string().min(1, 'Role required'),
    teams: z.array(z.string()),
    licenceType: z.string().pipe(z.enum(AgentDetailsLicenceType)),
    useLicenseeDetails: z.boolean(),
    instructionsConditions: z.string().max(1100, 'Use up to 1,100 characters'),
    emailInvitationSent: z.boolean(),
    softDeleted: z.boolean(),
    photo: z.custom<UploadedDocument>().optional(),
    agentCompanyDetails: z.object({
      licenseeName: z.string(),
      abn: z.string(),
      acn: z.string(),
      licences: z.array(z.custom<LicenceDetails>()),
    }),
    // Date controls need an explicit empty value. Other licence entries remain in the snapshot.
    licenceNumber: z.string(),
    expiryDate: z.string().nullable(),
    rebateSettings: z.object({
      mode: z.enum(AgentRebateSettingsMode),
      rebates: z.array(z.custom<Rebate>()),
    }),
    licenceStates: z.array(z.enum(States)),
    rebatesAvailable: z.boolean(),
  })
  .superRefine((values, ctx) => {
    const licenseeActive =
      values.useLicenseeDetails && canEditLicenseeDetails(values.licenceType, values.licenceStates);
    if (licenseeActive) {
      for (const [name, validate] of [
        ['abn', isValidABN],
        ['acn', isValidACN],
      ] as const) {
        const value = values.agentCompanyDetails[name];
        if (value && !validate(value.replace(/\s/g, '')))
          ctx.addIssue({
            code: 'custom',
            path: ['agentCompanyDetails', name],
            message: `${name.toUpperCase()} provided is not valid`,
          });
      }
      let validDate = false;
      try {
        validDate =
          !!values.expiryDate && toDateOnlyString(values.expiryDate) === values.expiryDate;
      } catch {
        /* Invalid date text belongs to this field. */
      }
      if (!validDate)
        ctx.addIssue({
          code: 'custom',
          path: ['expiryDate'],
          message: 'Enter a valid expiry date',
        });
    }
    if (
      values.rebatesAvailable &&
      values.rebateSettings.mode === AgentRebateSettingsMode.AgentSpecific
    ) {
      const result = rebatesSchema.safeParse(values.rebateSettings.rebates);
      if (!result.success)
        for (const issue of result.error.issues)
          ctx.addIssue({ ...issue, path: ['rebateSettings', 'rebates', ...issue.path] });
    }
  });

export type TeamMemberValues = z.input<typeof teamMemberSchema>;

export const teamMemberDefaults = (
  agent: AgentDetails = DefaultTeamMember,
  licenceStates: States[] = [],
  rebatesAvailable = true
): TeamMemberValues => ({
  id: agent.id ?? '',
  agencyId: agent.agencyId ?? '',
  eTag: agent.eTag,
  firstName: agent.firstName ?? '',
  lastName: agent.lastName ?? '',
  email: agent.email ?? '',
  title: agent.title ?? '',
  phone: agent.phone ?? '',
  role: agent.role ?? DefaultTeamMember.role ?? '',
  teams: [...(agent.teams ?? [])],
  licenceType: agent.licenceType ?? AgentDetailsLicenceType.None,
  useLicenseeDetails: agent.useLicenseeDetails ?? false,
  instructionsConditions: agent.instructionsConditions ?? '',
  emailInvitationSent: agent.emailInvitationSent ?? false,
  softDeleted: agent.softDeleted ?? false,
  photo: agent.photo,
  agentCompanyDetails: {
    licenseeName: agent.agentCompanyDetails?.licenseeName ?? '',
    abn: agent.agentCompanyDetails?.abn ?? '',
    acn: agent.agentCompanyDetails?.acn ?? '',
    licences: structuredClone(agent.agentCompanyDetails?.licences ?? []),
  },
  licenceNumber: agent.agentCompanyDetails?.licences?.[0]?.licenceNumber ?? '',
  expiryDate: agent.agentCompanyDetails?.licences?.[0]?.expiryDate ?? null,
  rebateSettings: structuredClone(
    agent.rebateSettings ?? { mode: AgentRebateSettingsMode.AgencyDefaults, rebates: [] }
  ),
  licenceStates,
  rebatesAvailable,
});

export const teamMemberRequest = (values: TeamMemberValues): AgentDetails => {
  const {
    licenceStates: _licenceStates,
    rebatesAvailable,
    licenceNumber,
    expiryDate,
    ...details
  } = values;
  const licences = details.agentCompanyDetails.licences;
  const licenceChanged =
    licenceNumber !== (licences[0]?.licenceNumber ?? '') ||
    expiryDate !== (licences[0]?.expiryDate ?? null);
  return {
    ...details,
    licenceType: z.enum(AgentDetailsLicenceType).parse(details.licenceType),
    agentCompanyDetails: {
      ...details.agentCompanyDetails,
      licences: licenceChanged
        ? [
            { ...licences[0], licenceNumber, expiryDate: expiryDate ?? undefined },
            ...licences.slice(1),
          ]
        : licences,
    },
    rebateSettings:
      rebatesAvailable && details.rebateSettings.mode === AgentRebateSettingsMode.AgencyDefaults
        ? { mode: AgentRebateSettingsMode.AgencyDefaults, rebates: [] }
        : rebatesAvailable
          ? {
              ...details.rebateSettings,
              rebates: rebatesSchema.parse(details.rebateSettings.rebates),
            }
          : details.rebateSettings,
  };
};

export const TeamMemberForm = createAgxForm(
  createFormDefinition({
    id: 'agency-team-member',
    schema: teamMemberSchema,
    fieldOrder: [
      'firstName',
      'lastName',
      'email',
      'role',
      'agentCompanyDetails.abn',
      'agentCompanyDetails.acn',
      'expiryDate',
      'instructionsConditions',
      'rebateSettings',
    ],
    resolveFields: (values) => {
      const licenseeActive =
        values.useLicenseeDetails &&
        canEditLicenseeDetails(values.licenceType, values.licenceStates);
      const required = { active: true, visible: true, disabled: false, required: true };
      return {
        firstName: required,
        lastName: required,
        email: required,
        role: required,
        expiryDate: { ...required, active: licenseeActive, visible: licenseeActive },
        ...Object.fromEntries(
          values.rebateSettings.rebates.flatMap((_, index) =>
            rebateFields.map(({ name }) => [`rebateSettings.rebates[${index}].${name}`, required])
          )
        ),
      };
    },
  })
);
export type TeamMemberFormInstance = ReturnType<typeof TeamMemberForm.useForm>;
